import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiSidebar, FiX } from "react-icons/fi";
import useIsMobile from "../../hooks/useIsMobile";

// A side panel a page can hang on either edge: hover peeks it open, click pins it, and below lg — where there is no hover — the same control opens a tap-to-dismiss sheet instead.

/** Below lg the rail is a tap-to-open sheet — hover-to-peek does not exist on touch. */
const COMPACT_BREAKPOINT = 1024;

/** Grace period so the pointer can cross the gap from the trigger into the panel. */
const PEEK_CLOSE_DELAY = 180;

/** Pinned and peeking share one width, so pinning is a shadow drop rather than a jump. */
const RAIL_WIDTH = "w-[288px]";

// The rail's two layers. They sit above the highest z-index a hosting page's own content uses (AssetGraph's header popup is z-[100]) and well below the modal layer at 10050. The toggle must outrank the panel: it lives in the centre column but paints ON the panel, which is only possible while both compete in the same stacking context — so no ancestor between the toggle and the row may create one of its own. It takes that rank only while peeking; see PeekRailToggle.
const PEEK_PANEL_Z = "z-[110]";
const PEEK_TOGGLE_Z = "z-[120]";
/** The standalone toggle's hover label floats over whatever the host paints below its bar. */
const TOGGLE_LABEL_Z = "z-[130]";

export interface PeekRailController {
  isCompact: boolean;
  pinned: boolean;
  isPeeking: boolean;
  overlayOpen: boolean;
  /** On screen in either mode — what an opener should report as `aria-expanded`. */
  isOpen: boolean;
  openPeek: () => void;
  closePeekSoon: () => void;
  toggle: () => void;
  /** Close the transient states (peek and sheet) without unpinning. */
  dismiss: () => void;
}

export const usePeekRail = (storageKey: string): PeekRailController => {
  const isCompact = useIsMobile(COMPACT_BREAKPOINT);
  const [pinned, setPinned] = useState(() => localStorage.getItem(storageKey) === "true");
  const [isPeeking, setIsPeeking] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const peekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openPeek = useCallback(() => {
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    setIsPeeking(true);
  }, []);

  const closePeekSoon = useCallback(() => {
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    peekTimerRef.current = setTimeout(() => setIsPeeking(false), PEEK_CLOSE_DELAY);
  }, []);

  useEffect(
    () => () => {
      if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    },
    []
  );

  const dismiss = useCallback(() => {
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    setIsPeeking(false);
    setOverlayOpen(false);
  }, []);

  // One control, two behaviours: a sheet on touch, peek/pin on a pointer device
  const toggle = useCallback(() => {
    if (isCompact) {
      setOverlayOpen((prev) => !prev);
      return;
    }
    const next = !pinned;
    setPinned(next);
    setIsPeeking(false);
    localStorage.setItem(storageKey, String(next));
  }, [isCompact, pinned, storageKey]);

  return {
    isCompact,
    pinned,
    isPeeking,
    overlayOpen,
    isOpen: isCompact ? overlayOpen : pinned || isPeeking,
    openPeek,
    closePeekSoon,
    toggle,
    dismiss,
  };
};

interface PeekRailToggleProps {
  rail: PeekRailController;
  /** Which edge the rail sits on — the icon mirrors for the right. */
  side?: "left" | "right";
  label: string;
  /** Toggle sits in a bar of its own, outside the panel's reach — it keeps its frame and shows its label on hover. */
  standalone?: boolean;
  className?: string;
  [key: string]: any;
}

/** Lives at the leading edge of the centre column's own controls, not inside the rail, so it stays reachable when the rail is closed and the pointer can travel straight from it into the panel. */
export const PeekRailToggle = ({
  rail,
  side = "left",
  label,
  standalone = false,
  className = "",
  ...rest
}: PeekRailToggleProps) => {
  const isLeft = side === "left";
  const isOpen = rail.isCompact ? rail.overlayOpen : rail.pinned;

  return (
    <button
      type="button"
      onClick={rail.toggle}
      onMouseEnter={rail.isCompact ? undefined : rail.openPeek}
      onMouseLeave={rail.isCompact ? undefined : rail.closePeekSoon}
      aria-expanded={isOpen}
      /* Standalone draws its own hover label, so the native tooltip would only duplicate it a second later */
      title={standalone ? undefined : label}
      aria-label={label}
      className={`eai-btn-toolbar-icon relative flex-none ${
        standalone
          ? "group"
          : // Raised only while there is a panel to paint over; held always it outranks a host's own covers
          rail.isPeeking
          ? PEEK_TOGGLE_Z
          : ""
      } ${
        !standalone && rail.isPeeking
          ? // Sitting ON the panel — drop the frame so it reads as part of that corner, not a chip stuck to it
            "border-transparent bg-transparent text-eai-ink hover:border-transparent hover:bg-eai-surface-hover"
          : isOpen
          ? "border-eai-border-soft-hover bg-eai-surface-hover text-eai-ink"
          : ""
      } ${className}`}
      {...rest}
    >
      <FiSidebar className={`h-4 w-4 shrink-0 ${side === "right" ? "-scale-x-100" : ""}`} />
      {standalone && (
        /* eai-card at mt-1.5 — what every menu that floats out of a control uses, the two in this bar included; hover-only, and Tailwind's hover: is already off on touch where the aria-label carries the name */
        <span className={`eai-card ${TOGGLE_LABEL_Z} pointer-events-none absolute top-full ${
          isLeft ? "left-0" : "right-0"
        } mt-1.5 whitespace-nowrap px-3 py-1.5 font-outfit text-eai-control text-eai-ink-muted opacity-0 transition-opacity group-hover:opacity-100`}>
          {label}
        </span>
      )}
    </button>
  );
};

interface PeekRailProps {
  rail: PeekRailController;
  side: "left" | "right";
  /** Eyebrow shown on the peek and sheet headers. */
  title: string;
  children: React.ReactNode;
  /** Optional pinned strip below the scroll area, e.g. a secondary action. */
  footer?: React.ReactNode;
  /** Height/padding of the peek header — the strip that reserves the toggle's lane. The host sets it to mirror whatever control row its toggle sits in, so the two line up. */
  peekHeaderClassName?: string;
  /** Off when the host's toggle sits outside the panel — nothing floats over the header to make room for. */
  reserveToggleLane?: boolean;
  testId?: string;
}

/** Renders all three states of one rail. Drop it in as a child of the `relative flex` row: the pinned aside is a flex item, the peek is absolute within that row, and the compact sheet is fixed. For a right rail, place it after the centre column. */
export const PeekRail = ({
  rail,
  side,
  title,
  children,
  footer,
  peekHeaderClassName = "h-eai-toolbar",
  reserveToggleLane = true,
  testId,
}: PeekRailProps) => {
  const isLeft = side === "left";
  const edgeBorder = isLeft ? "border-r" : "border-l";

  useEffect(() => {
    if (!rail.overlayOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") rail.dismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [rail]);

  return (
    <>
      {/* Pinned — the click-to-stay state. Unpinned, the centre column runs full width and the same panel appears on hover as the sheet below. */}
      {rail.pinned && (
        <aside
          data-testid={testId ? `${testId}-pinned` : undefined}
          className={`hidden h-full min-h-0 ${RAIL_WIDTH} flex-none flex-col ${edgeBorder} border-eai-border bg-eai-surface-card lg:flex`}
        >
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
          {footer}
        </aside>
      )}

      {/* Hover peek — flush to the card edges and the same width as the pinned aside, so pinning is a shadow drop rather than a jump */}
      {!rail.pinned && !rail.isCompact && rail.isPeeking && (
        <div
          onMouseEnter={rail.openPeek}
          onMouseLeave={rail.closePeekSoon}
          data-testid={testId ? `${testId}-peek` : undefined}
          className={`absolute inset-y-0 ${isLeft ? "left-0" : "right-0"} ${PEEK_PANEL_Z} flex ${RAIL_WIDTH} flex-col overflow-hidden ${edgeBorder} border-eai-border bg-eai-surface-card ${
            isLeft ? "shadow-eai-peek-right" : "shadow-eai-peek-left"
          }`}
        >
          {/* Reserves the toggle's lane — 44px — and takes its height from the host so the eyebrow lands on the same line as that floating toggle */}
          <div
            className={`flex flex-none items-center ${
              reserveToggleLane ? (isLeft ? "pl-11 pr-3" : "pl-3 pr-11") : "px-3"
            } ${peekHeaderClassName}`}
          >
            <span className="eai-eyebrow">{title}</span>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
          {footer}
        </div>
      )}

      {/* Touch — a tap-to-dismiss sheet, since there is no hover to peek with. Sits below the modal layer (10050) so a modal opened from the panel still covers it. */}
      {rail.isCompact && rail.overlayOpen && (
        <div className="fixed inset-0 z-[9000] flex lg:hidden">
          <button
            type="button"
            aria-label={`Close ${title}`}
            onClick={rail.dismiss}
            className="absolute inset-0 bg-black/40"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            data-testid={testId ? `${testId}-overlay` : undefined}
            className={`relative flex h-full w-[min(88vw,320px)] flex-col bg-eai-surface-card ${
              isLeft ? "mr-auto shadow-eai-peek-right" : "ml-auto shadow-eai-peek-left"
            }`}
          >
            <div className="flex h-eai-toolbar flex-none items-center justify-between border-b border-eai-border px-4">
              <span className="eai-eyebrow">{title}</span>
              <button type="button" onClick={rail.dismiss} className="eai-btn-icon" aria-label="Close">
                <FiX className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">{children}</div>
            {footer}
          </div>
        </div>
      )}
    </>
  );
};

export default PeekRail;
